// GateRunDetail — the gate-run detail Drawer content required by docs/09.
// Fetched via GET /v1/gate-runs/{id} seeded from the list cache, so a
// `?run=<id>` deep link restores on a cold load without the list.

import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import {
  Badge,
  Button,
  Code,
  Copyable,
  Inline,
  InlineError,
  KeyValue,
  Section,
  Skeleton,
  Stack,
} from "../ui";

type GateRun = components["schemas"]["GateRunResponse"];

// Shared with the Alerts gate-run table; the drawer owns the verdict tone.
export function resultTone(run: GateRun): "success" | "danger" {
  return run.verdict === "pass" ? "success" : "danger";
}

// Seed the detail query from any cached gate-runs list page, so opening a
// row the table already holds renders instantly (no drawer skeleton).
function seedFromListCache(
  queryClient: QueryClient,
  gateRunId: string,
): GateRun | undefined {
  const lists = queryClient.getQueriesData<InfiniteData<{ items: GateRun[] }>>({
    queryKey: queryKeys.gateRuns.list(),
  });
  for (const [, data] of lists) {
    const match = data?.pages
      .flatMap((page) => page.items)
      .find((run) => run.gate_run_id === gateRunId);
    if (match) {
      return match;
    }
  }
  return undefined;
}

export interface GateRunDetailProps {
  gateRunId: string;
  onClose: () => void;
}

export function GateRunDetail({ gateRunId, onClose }: GateRunDetailProps) {
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: queryKeys.gateRuns.detail(gateRunId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/gate-runs/{id}", {
        params: { path: { id: gateRunId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    initialData: () => seedFromListCache(queryClient, gateRunId),
  });

  return (
    <GateRunDetailView
      errorMessage={
        detail.isError
          ? requestErrorDescription(detail.error, "Gate request failed.")
          : undefined
      }
      isPending={detail.isPending}
      onClose={onClose}
      run={detail.data}
    />
  );
}

export interface GateRunDetailViewProps {
  errorMessage: string | undefined;
  isPending: boolean;
  onClose: () => void;
  run: GateRun | undefined;
}

export function GateRunDetailView({
  errorMessage,
  isPending,
  onClose,
  run,
}: GateRunDetailViewProps) {
  if (isPending) {
    return <Skeleton label="Loading gate run" rows={9} shape="detail-field" />;
  }

  if (errorMessage !== undefined || run === undefined) {
    // Invalid/unknown id renders the drawer error state, never a blank page
    // (gates.md); the list behind stays live.
    return (
      <Stack gap={3}>
        <InlineError>{errorMessage ?? "Gate run not found."}</InlineError>
        <Inline>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Inline>
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <Inline gap={3}>
        <Badge tone={resultTone(run)}>{run.verdict}</Badge>
        <Badge>{run.status}</Badge>
      </Inline>
      <KeyValue
        rows={[
          { label: "Measure", value: run.measure },
          { label: "Comparator", value: run.comparator },
          { label: "Threshold", value: String(run.threshold) },
          { label: "Value", value: run.value.toFixed(4) },
          { label: "Occurrences", value: String(run.occurrences) },
          { label: "Evaluation mode", value: run.evaluation_mode },
          { label: "Evaluated at", value: formatDateTime(run.evaluated_at) },
          { label: "Created by", value: run.created_by ?? "—" },
          {
            label: "Gate run ID",
            value: <Copyable label="Gate run ID" value={run.gate_run_id} />,
          },
        ]}
      />
      <Section heading="Target">
        <Code block>{JSON.stringify(run.target, null, 2)}</Code>
      </Section>
    </Stack>
  );
}
